import CardTable, {
  type CardTableRowRendererProps,
} from '@/components/Tables/CardTable';
import { CircularProgress, Tag, useLocaleFormat } from '@eye-able/ui';
import type { ColumnDefMeta, TableRef } from '@eye-able/ui';
import { FileText, Target, TriangleAlert } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { forwardRef } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { currentAuditEntryIdAtom } from '@/global-store';
import { useAtom } from 'jotai/react';

export type ReportRow = {
  id?: number;
  domain?: string;
  summaryDetailScore?: number | string;
  conformanceTarget?: string;
  lastTotalPagesChecked?: number;
  lastAvgErrorCount?: number;
};

export interface HomeReportTableProps {
  data: ReportRow[];
}

const HomeReportTable = forwardRef<TableRef<ReportRow>, HomeReportTableProps>(
  function HomeReportTable({ data }, ref) {
    const columns = useColumns();

    const onRowClick = useOnRowClick();

    return (
      <CardTable
        ref={ref}
        data={data}
        columns={columns}
        getRowId={(row: ReportRow, index: number) =>
          row?.id ?? row?.domain ?? index
        }
        rowTitle={RowTitle}
        rowMain={RowMain}
        searchColumnId='domain'
        defaultSortBy='summaryDetailScore'
        defaultSortDirection='desc'
        onRowClick={onRowClick}
      />
    );
  },
);

const StaticInfo = ({ row }: { row: ReportRow }) => {
  const { formatNumber } = useLocaleFormat();

  function getConformanceLevelString(target?: string | null) {
    if (!target) return '';

    const levels = [];

    if (target.includes('AAA')) levels.push('AAA');
    else if (target.includes('AA')) levels.push('AA');
    else if (target.includes('A')) levels.push('A');

    if (target.includes('Best Practice')) levels.push('Best Practice');

    return levels.join(', ');
  }

  return (
    <div className='eye:flex eye:flex-wrap eye:items-center eye:gap-2xs eye:body-s'>
      <div className='eye:flex eye:items-center eye:gap-5xs'>
        <Target aria-hidden className='eye:stroke-[1.5]' />
        <span>{getConformanceLevelString(row.conformanceTarget)}</span>
      </div>
      <div className='eye:flex eye:items-center eye:gap-5xs'>
        <FileText aria-hidden className='eye:stroke-[1.5]' />
        <span>{row.lastTotalPagesChecked ?? 0}</span>
      </div>
      <div className='eye:flex eye:items-center eye:gap-5xs'>
        <TriangleAlert aria-hidden className='eye:stroke-[1.5]' />
        <span>{formatNumber(row.lastAvgErrorCount ?? 0)}</span>
      </div>
    </div>
  );
};

const RowTitle = ({ row, view }: CardTableRowRendererProps<ReportRow>) => {
  if (view === 'grid') {
    return (
      <div className='eye:flex eye:flex-col eye:gap-5xs'>
        <div>{row.domain}</div>
        <StaticInfo row={row} />
      </div>
    );
  }
  return row.domain;
};

const RowMain = ({ row, view }: CardTableRowRendererProps<ReportRow>) => {
  const { t } = useTranslation();

  const score = Math.min(
    Math.max(Number.parseFloat(String(row.summaryDetailScore)) || 0, 0),
    100,
  );

  if (view === 'grid') {
    return (
      <div className='eye:min-h-[140px] eye:p-s'>
        <CircularProgress
          value={score}
          ariaLabel={t('overallRating')}
          size='default'
        />
      </div>
    );
  }

  return (
    <div className='eye:flex eye:w-full eye:justify-between'>
      <StaticInfo row={row} />

      <CircularProgress
        value={score}
        ariaLabel={t('overallRating')}
        size='small'
      />
    </div>
  );
};

function useOnRowClick() {
  const navigate = useNavigate();
  const [, setCurrentAuditEntryId] = useAtom(currentAuditEntryIdAtom);

  return function onRowClick(row: ReportRow) {
    if (typeof row.id === 'number') {
      setCurrentAuditEntryId(row.id);
    }
    navigate('/report/overview');
  };
}

function useColumns() {
  const { t } = useTranslation();

  return useMemo<ColumnDef<ReportRow, unknown>[]>(() => {
    const parseConformanceTokens = (value?: string | null): string[] => {
      if (!value) return [];

      return value
        .split(/[+,]/)
        .map((token) => token.trim())
        .filter(Boolean);
    };
    return [
      {
        id: 'domain',
        accessorKey: 'domain',
        header: t('domain'),
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
          filterVariant: 'text',
        } as ColumnDefMeta,
      },
      {
        id: 'summaryDetailScore',
        accessorKey: 'summaryDetailScore',
        header: t('overallRating'),
        sortDescFirst: true,
        enableColumnFilter: false,
      },
      {
        id: 'conformanceTarget',
        accessorKey: 'conformanceTarget',
        header: t('target'),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValues) => {
          if (!Array.isArray(filterValues) || filterValues.length === 0) {
            return true;
          }

          const cellValue = row.getValue<string | null | undefined>(columnId);
          const tokens = parseConformanceTokens(cellValue);

          return filterValues.every((selected) =>
            tokens.includes(String(selected)),
          );
        },
        meta: {
          filterVariant: 'multiselect',
          filterOptions: ['A', 'AA', 'AAA', 'Best Practice'],
          filterRenderer: (item: { value: string }) => <Tag>{item.value}</Tag>,
        } as ColumnDefMeta,
      },
      {
        id: 'lastTotalPagesChecked',
        accessorKey: 'lastTotalPagesChecked',
        header: t('numberOfPages'),
        sortDescFirst: true,
        enableColumnFilter: true,
        meta: {
          filterVariant: 'range',
        } as ColumnDefMeta,
      },
      {
        id: 'lastAvgErrorCount',
        accessorKey: 'lastAvgErrorCount',
        header: t('averageErrorCount'),
        sortDescFirst: true,
        enableColumnFilter: true,
        meta: {
          filterVariant: 'range',
        } as ColumnDefMeta,
      },
    ];
  }, [t]);
}

export default HomeReportTable;
